import { Button, VStack } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { CapitalCallNotesCellRenderer } from "features/capital-call/drawdown/CapitalCallDrawdownFields";
import {
  getFundAllocationColumnData,
  getFundAllocationForFundFamilyDistributions,
  useGetFeeUpdateAllocationsByLookupKey,
} from "features/distributions/FundFamilyDistributionAllocationTableUtils";
import { FundFamilyDistributionFeeBreakCard } from "features/distributions/FundFamilyDistributionFeeBreakTable";
import { ManagementFeeHoldbackCard } from "features/distributions/FundFamilyDistributionFeeHoldbackCard";
import { FundFamilyDistributionPageTitle } from "features/distributions/FundFamilyDistributionPage";
import { useGetAllocationsByLookupKey } from "features/drawdown/DrawdownUtils";
import {
  getDistributionAdjustmentTableDataRetrieveQueryKey,
  getDistributionBreakdownTableDataRetrieveQueryKey,
  getDistributionUnfundedBreakdownTableDataRetrieveQueryKey,
  getFundFamilyDistributionRetrieveQueryKey,
  useFundFamilyDistributionCreate,
  useFundFamilyDistributionRetrieveSuspense,
} from "gen/endpoints/distributions/distributions";
import { useFeeListSuspense } from "gen/endpoints/fees/fees";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { useFundFamilyConfigRetrieveSuspense } from "gen/endpoints/fund-family-config/fund-family-config";
import {
  getFundFamilySubEventListQueryKey,
  useFundFamilyEventRetrieveSuspense,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { useInvestmentListSuspense } from "gen/endpoints/investment/investment";
import { useTransactionCodeConfigListSuspense } from "gen/endpoints/transactions/transactions";
import { CallOrDistributionEnum } from "gen/model/callOrDistributionEnum";
import { ClassificationEnum } from "gen/model/classificationEnum";
import { DistributionCreateDataRequest } from "gen/model/distributionCreateDataRequest";
import { FeeData } from "gen/model/feeData";
import { FundFamilyEventData } from "gen/model/fundFamilyEventData";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { FundFamilyUseAllocationTableAmount } from "gen/model/fundFamilyUseAllocationTableAmount";
import { FundFamilyUseAllocationTableAmountTypeDef } from "gen/model/fundFamilyUseAllocationTableAmountTypeDef";
import { FundFamilyUseAllocationTableData } from "gen/model/fundFamilyUseAllocationTableData";
import { FundFamilyUseAllocationTableDef } from "gen/model/fundFamilyUseAllocationTableDef";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { ManagementFeeDistributionMethodologyEnum } from "gen/model/managementFeeDistributionMethodologyEnum";
import { NegativeOrPositiveDistributionTransactionCodeNamesEnum } from "gen/model/negativeOrPositiveDistributionTransactionCodeNamesEnum";
import { SubClassificationEnum } from "gen/model/subClassificationEnum";
import { TransactionTypeEnum } from "gen/model/transactionTypeEnum";
import { fundFamilyDistributionCreateBody } from "gen/schemas/distributions/distributions";
import { Draft, produce, WritableDraft } from "immer";
import { cloneDeep, sum } from "lodash-es";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Form from "shared/components/form/Form";
import { FormErrorAlert } from "shared/components/form/FormErrorAlert";
import { FundFamilyUseAllocationTable } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTable";
import { FundFamilyUseAllocationTableAmountCellRenderer } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableAmountCellRenderer";
import { fundFamilyUseAllocationTableResolver } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableFormUtils";
import {
  FundFamilyUseAllocationTableAmountCellRendererSelector,
  FundFamilyUseAllocationTableAmountInfoCellRendererProps,
  FundFamilyUseAllocationTableColumnCellRendererSelector,
  FundFamilyUseAllocationTableOnBeforeRowChangeFn,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import { useFundFamilyUseAllocationTableFormRowController } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableRowController";
import { FundFamilyUseAllocationTableGenericAllocationRowAmounts } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableTypes";
import {
  createFundFamilyUseAllocationTableEmptyRow,
  isFundFamilyDef,
  useGetFundFamilyUseAllocationTableDataGetErrorForCellFnFromResponse,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableUtils";
import PageBody from "shared/components/layout/PageBody";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxActions } from "shared/components/table/box/TableBoxActions";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { useSortObjectArray } from "shared/hooks/useSortObjectArray";
import { decimalOrZero } from "shared/utils/DecimalUtils";
import { setFormValueIfChanged } from "shared/utils/FormUtils";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";
import { useBoolean } from "usehooks-ts";

const autoUpdateRemaining = (
  hasFeeUpdate: boolean,
  feeList: FeeData[] | undefined,
  amounts: Draft<FundFamilyUseAllocationTableGenericAllocationRowAmounts>,
) => {
  const distributionAmount = decimalOrZero(amounts.distribution_amount.amount);
  const feeUpdateAmount =
    hasFeeUpdate && distributionAmount.greaterThan(0)
      ? sum(
          feeList?.map((fee) => {
            const feeAmount = amounts[fee.id] as
              | WritableDraft<FundFamilyUseAllocationTableAmount>
              | undefined;
            return decimalOrZero(feeAmount?.amount);
          }) ?? [],
        )
      : new Decimal(0);
  const amount = distributionAmount.minus(feeUpdateAmount).toFixed(2);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (amounts.remaining_amount) {
    amounts.remaining_amount.amount = amount;
  } else {
    amounts.remaining_amount = { amount };
  }
};

const autoUpdateFundDistributionAmount = (
  hasFeeUpdate: boolean,
  feeList: FeeData[] | undefined,
  amounts: Draft<FundFamilyUseAllocationTableGenericAllocationRowAmounts>,
) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const remainingAmount = decimalOrZero(amounts.remaining_amount?.amount);
  const feeUpdateAmount = hasFeeUpdate
    ? sum(
        feeList?.map((fee) => {
          const feeAmount = amounts[fee.id] as
            | WritableDraft<FundFamilyUseAllocationTableAmount>
            | undefined;
          return decimalOrZero(feeAmount?.amount);
        }) ?? [],
      )
    : new Decimal(0);
  const amount = remainingAmount.plus(feeUpdateAmount).toFixed(2);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (amounts.distribution_amount) {
    amounts.distribution_amount.amount = amount;
  } else {
    amounts.distribution_amount = { amount };
  }
};

const FundFamilyUseAllocationTableForm: React.FC<{
  fundFamilyId: string;
  fundFamilyEventId: string;
  fundFamilyEventData: FundFamilyEventData;
}> = ({ fundFamilyId, fundFamilyEventId, fundFamilyEventData }) => {
  const { t } = useTranslation(["common", "distribution"]);
  const queryClient = useQueryClient();

  const {
    mutateAsync: createFundFamilyDistribution,
    error: createError,
    variables: createVariables,
    reset: resetCreate,
  } = useFundFamilyDistributionCreate();

  const { data: distributionCreateData } =
    useFundFamilyDistributionRetrieveSuspense(fundFamilyId, fundFamilyEventId);

  const draftData: FundFamilyUseAllocationTableData | undefined =
    distributionCreateData.data;

  const { data: fundFamilyConfigData } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  const { value: hasFeeUpdate, toggle: toggleFeeUpdate } = useBoolean(
    draftData.rows.some((row) =>
      Object.values(row.fund_family).some(
        (amount) =>
          amount.meta?.type ===
            fundFamilyConfigData.config
              .management_fee_distribution_methodology && amount.meta.fee_id,
      ),
    ),
  );
  const { data: feeList } = useFeeListSuspense(fundFamilyId);

  const { data: transactionCodeConfigList } =
    useTransactionCodeConfigListSuspense(fundFamilyId, {
      call_or_distribution: CallOrDistributionEnum.distribution,
      transaction_type: TransactionTypeEnum.capital,
      exclude_sub_classifications: [SubClassificationEnum.carried_interest],
      exclude_classifications: [ClassificationEnum.equalization],
    });

  const { data: investmentList } = useInvestmentListSuspense(fundFamilyId, {
    is_allocable: true,
  });

  const orderedInvestments = useSortObjectArray(investmentList, "legal_name");

  const { data: fundList } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
    require_commitment: true,
  });

  const initialFeeChargeEventIds = useMemo(
    () =>
      Object.values(
        distributionCreateData.fee_id_to_fee_charge_event_ids,
      ).reduce(
        (feeChargeEventIds, currentIds) => [
          ...feeChargeEventIds,
          ...currentIds,
        ],
        [],
      ),
    [distributionCreateData.fee_id_to_fee_charge_event_ids],
  );

  const [feeChargeEventIds, setFeeChargeEventIds] = useState<string[]>(
    initialFeeChargeEventIds,
  );

  const hasFeeBreakDistribution =
    hasFeeUpdate &&
    fundFamilyConfigData.config.management_fee_distribution_methodology ==
      ManagementFeeDistributionMethodologyEnum.fee_break_distribution;

  const distributionDef = useMemo<FundFamilyUseAllocationTableDef>(
    () => ({
      enable_row_add_remove: true,
      show_total_row: true,
      total_title: t("distribution:totalDistributionAmount"),
      column_data_type: "distribution",
      headers: [
        {
          id: "transaction_code_id",
          title: t("distribution:cashToDistribute.transactionTag"),
          width: 384,
          editor: {
            type: "select",
            placeholder: t("distribution:selectTransactionTag"),
            options: transactionCodeConfigList.map((transactionCodeConfig) => ({
              value: transactionCodeConfig.transaction_code_id,
              label: transactionCodeConfig.name,
            })),
          },
        },
        {
          id: "investment_id",
          title: t("distribution:investment"),
          width: 300,
          editor: {
            type: "select",
            placeholder: t("distribution:selectInvestment"),
            options: orderedInvestments.map((investment) => ({
              value: investment.id,
              label: investment.legal_name,
            })),
          },
        },
      ],
      trailers: [
        {
          id: "notes",
          title: t("distribution:notes"),
          width: 300,
        },
      ],
      categories: [
        {
          id: "distribution",
          allow_auto_allocate: true,
          allow_multiple: true,
        },
      ],
      amount_types: [
        {
          id: "distribution_amount",
          title: t("distribution:cashToDistribute.pageName"),
        },
        ...(hasFeeUpdate
          ? [
              ...feeList.map(
                (fee): FundFamilyUseAllocationTableAmountTypeDef => ({
                  id: fee.id,
                  title: t(
                    `distribution:feeUpdates.${
                      fundFamilyConfigData.config
                        .management_fee_distribution_methodology as "fee_holdback"
                    }.feeBreakAmountWithName`,
                    {
                      name: fee.name,
                    },
                  ),
                  meta: {
                    type: fundFamilyConfigData.config
                      .management_fee_distribution_methodology,
                    fee_id: fee.id,
                  },
                }),
              ),
              {
                id: "remaining_amount",
                title: t("distribution:remainingDistributionAmount"),
                is_read_only: !hasFeeBreakDistribution,
              },
            ]
          : []),
      ],
      fund_family: {
        fund_family_id: fundFamilyId,
        title: t("common:fundFamily"),
      },
      funds: fundList.map((fund) => ({
        fund_id: fund.id,
        title: fund.legal_name,
      })),
    }),
    [
      t,
      transactionCodeConfigList,
      orderedInvestments,
      hasFeeUpdate,
      feeList,
      hasFeeBreakDistribution,
      fundFamilyId,
      fundList,
      fundFamilyConfigData.config.management_fee_distribution_methodology,
    ],
  );

  const populateEmptyRow = (
    newRow: FundFamilyUseAllocationTableRow,
  ): FundFamilyUseAllocationTableRow => ({
    ...newRow,
    column_data: {
      type: "distribution",
      transaction_code_id: null as unknown as string, // need fields to be null for auto allocation
      investment_id: null,
      notes: "",
    },
  });

  const initialRows = useMemo(() => {
    const newRows = draftData.rows.length
      ? cloneDeep(draftData.rows)
      : [
          populateEmptyRow(
            createFundFamilyUseAllocationTableEmptyRow(distributionDef),
          ),
        ];
    newRows.forEach((row) => {
      autoUpdateRemaining(hasFeeUpdate, feeList, row.fund_family);
      Object.values(row.funds).forEach((fundAmounts) => {
        if (hasFeeBreakDistribution) {
          autoUpdateFundDistributionAmount(hasFeeUpdate, feeList, fundAmounts);
        } else {
          autoUpdateRemaining(hasFeeUpdate, feeList, fundAmounts);
        }
      });
    });
    return newRows;
  }, [
    distributionDef,
    draftData.rows,
    feeList,
    hasFeeUpdate,
    hasFeeBreakDistribution,
  ]);

  const { navigateNext } = usePageWizard();

  const feeId = feeList[0]?.id;

  const resolver = useMemo(
    () =>
      fundFamilyUseAllocationTableResolver(
        zodResolver(
          fundFamilyDistributionCreateBody.superRefine((arg, ctx) => {
            arg.data.rows.forEach((row) => {
              const columnData = getFundAllocationColumnData(
                row as FundFamilyUseAllocationTableRow | undefined,
              );
              const txCodeConfig = transactionCodeConfigList.find(
                (it) =>
                  it.transaction_code_id === columnData?.transaction_code_id,
              );
              const allowNegative =
                txCodeConfig?.name &&
                Object.values(
                  NegativeOrPositiveDistributionTransactionCodeNamesEnum,
                ).includes(
                  txCodeConfig.name as NegativeOrPositiveDistributionTransactionCodeNamesEnum,
                );
              const rowPath = ["data", "rows", row.id];
              Object.entries(row.fund_family).forEach(([amountId, amount]) => {
                if (Number(amount.amount) < 0 && !allowNegative) {
                  ctx.addIssue({
                    code: "custom",
                    path: [...rowPath, "fund_family", amountId, "amount"],
                    message: t("distribution:errors.amountMustBePositive"),
                  });
                }
              });
              Object.entries(row.funds).forEach(([fundId, fundAmounts]) => {
                Object.entries(fundAmounts).forEach(([amountId, amount]) => {
                  if (Number(amount.amount) < 0 && !allowNegative) {
                    ctx.addIssue({
                      code: "custom",
                      path: [...rowPath, "funds", fundId, amountId, "amount"],
                      message: t("distribution:errors.amountMustBePositive"),
                    });
                  }
                });
              });
            });
          }),
        ),
        "data.rows",
      ),
    [t, transactionCodeConfigList],
  );

  const form = useForm<DistributionCreateDataRequest>({
    resolver,
    defaultValues: {
      data: {
        rows: initialRows,
      },
      fee_id_to_fee_charge_event_ids: { [feeId]: initialFeeChargeEventIds },
    },
  });

  const { rows, onRowChange, onRowAdd, onRowRemove } =
    useFundFamilyUseAllocationTableFormRowController(
      form,
      "data.rows",
      populateEmptyRow,
    );

  useEffect(() => {
    setFormValueIfChanged(
      form,
      "fee_id_to_fee_charge_event_ids",
      { [feeId]: feeChargeEventIds },
      { shouldDirty: true },
    );
  }, [form, feeId, feeChargeEventIds]);

  const getErrorForCell =
    useGetFundFamilyUseAllocationTableDataGetErrorForCellFnFromResponse(
      createError,
      useCallback(
        (rowId) =>
          createVariables?.data.data.rows.findIndex((it) => it.id === rowId),
        [createVariables],
      ),
    );

  const onSubmit = async (data: DistributionCreateDataRequest) => {
    await createFundFamilyDistribution({
      fundFamilyId,
      fundFamilyEventId,
      data,
    });
    await queryClient.invalidateQueries({
      queryKey: getFundFamilyDistributionRetrieveQueryKey(
        fundFamilyId,
        fundFamilyEventId,
      ),
    });
  };
  const invalidateQueries = useInvalidateQueryKeys();
  const onSuccess = async () => {
    await invalidateQueries(
      getDistributionBreakdownTableDataRetrieveQueryKey(
        fundFamilyId,
        fundFamilyEventId,
      ),

      getDistributionUnfundedBreakdownTableDataRetrieveQueryKey(
        fundFamilyId,
        fundFamilyEventId,
      ),

      getDistributionAdjustmentTableDataRetrieveQueryKey(
        fundFamilyId,
        fundFamilyEventId,
      ),
      getFundFamilySubEventListQueryKey(fundFamilyId, fundFamilyEventId),
    );
    navigateNext();
  };

  const getAllocationsByLookupKey = useGetAllocationsByLookupKey(
    fundFamilyId,
    rows.map((row) => {
      const columnData = getFundAllocationColumnData(row);
      return {
        investment_id: columnData?.investment_id || null,
        transaction_code_id: columnData?.transaction_code_id || null,
      };
    }),
    fundFamilyEventData.effective_date,
  );

  const getFeeUpdateAllocationsByLookupKey =
    useGetFeeUpdateAllocationsByLookupKey(
      rows.map((row) => ({
        investment_id: getFundAllocationColumnData(row)!.investment_id ?? null,
        transaction_code_id:
          getFundAllocationColumnData(row)!.transaction_code_id,
      })),
      feeId,
      feeChargeEventIds,
      fundFamilyConfigData.config.management_fee_distribution_methodology,
    );

  const onBeforeRowChange =
    useCallback<FundFamilyUseAllocationTableOnBeforeRowChangeFn>(
      ({ row }) =>
        cloneDeep(
          produce(cloneDeep(row), (draft) => {
            const updateFeeChanges = (
              fundAmounts: Draft<FundFamilyUseAllocationTableGenericAllocationRowAmounts>,
            ) => {
              if (hasFeeUpdate) {
                const distributionAmount =
                  fundAmounts.distribution_amount.amount;
                Object.entries(fundAmounts).forEach(
                  ([fundAmountId, fundAmount]) => {
                    const amountType = distributionDef.amount_types.find(
                      (it) => it.id === fundAmountId,
                    );
                    if (
                      amountType?.meta?.type ===
                      fundFamilyConfigData.config
                        .management_fee_distribution_methodology
                    ) {
                      fundAmount.meta = amountType.meta;
                      // we do this to handle negative distribution amounts (which should removes the fee amount), but we still want to pass validation without making the amount field optional on the upsert data.
                      // This also lets sums to be accurate when distribution amount is negative.
                      if (decimalOrZero(distributionAmount).lessThan(0)) {
                        fundAmount.amount = "0.00";
                      }
                    }
                  },
                );
              } else {
                for (const [fundAmountId, fundAmount] of Object.entries(
                  fundAmounts,
                )) {
                  if (
                    fundAmount.meta?.type ===
                    fundFamilyConfigData.config
                      .management_fee_distribution_methodology
                  ) {
                    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                    delete fundAmounts[fundAmountId];
                  }
                }
              }
            };
            autoUpdateRemaining(hasFeeUpdate, feeList, draft.fund_family); // this should use auto allocate endpoints
            updateFeeChanges(draft.fund_family);
            Object.values(draft.funds).forEach((fundAmounts) => {
              if (hasFeeBreakDistribution) {
                autoUpdateFundDistributionAmount(
                  hasFeeUpdate,
                  feeList,
                  fundAmounts,
                );
              } else {
                autoUpdateRemaining(hasFeeUpdate, feeList, fundAmounts);
              }
              updateFeeChanges(fundAmounts);
            });
          }),
        ),
      [
        distributionDef.amount_types,
        feeList,
        hasFeeUpdate,
        fundFamilyConfigData.config,
        hasFeeBreakDistribution,
      ],
    );

  const tableData = useMemo<FundFamilyUseAllocationTableData>(
    () => ({ rows }),
    [rows],
  );

  const amountCellRendererSelector: FundFamilyUseAllocationTableAmountCellRendererSelector =
    useCallback(
      (params) => {
        const distributionAmount = isFundFamilyDef(params.fundFamilyOrFundDef)
          ? decimalOrZero(params.row.fund_family.distribution_amount.amount)
          : decimalOrZero(
              params.row.funds[params.fundFamilyOrFundDef.fund_id]
                .distribution_amount.amount,
            );

        // if there is a meta object, it means it is the fee amount that is optionally removed on a negative distribution amount
        if (params.amountTypeDef.meta && distributionAmount.lessThan(0)) {
          return null;
        }

        if (hasFeeBreakDistribution) {
          return CustomFeeBreakDistributionAmountCellRenderer;
        }
      },
      [hasFeeBreakDistribution],
    );

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      onSuccess={onSuccess}
      onBeforeSubmit={resetCreate}
      onError={() => true}
      excludeNonFieldErrors={["data", "fee_id_to_fee_charge_event_ids"]}
    >
      <FormErrorAlert error={createError} />
      {hasFeeUpdate &&
        (fundFamilyConfigData.config.management_fee_distribution_methodology ===
        ManagementFeeDistributionMethodologyEnum.fee_holdback ? (
          <ManagementFeeHoldbackCard
            tableRows={rows}
            setFeeChargeEventIds={setFeeChargeEventIds}
            feeChargeEventIds={feeChargeEventIds}
            closeComponent={toggleFeeUpdate}
            funds={fundList}
            feeId={feeId}
          />
        ) : (
          <FundFamilyDistributionFeeBreakCard
            tableRows={rows}
            closeComponent={toggleFeeUpdate}
          />
        ))}
      <TableBox isCollapsible={hasFeeUpdate} isFullWidth={true}>
        <TableBoxHeader>
          <TableBoxTitle>
            {t("distribution:cashToDistribute.distributableCash")}
          </TableBoxTitle>
          <TableBoxActions>
            {!hasFeeUpdate &&
              fundFamilyConfigData.config
                .management_fee_distribution_methodology ==
                ManagementFeeDistributionMethodologyEnum.fee_holdback && (
                <Button
                  size="xs"
                  colorScheme="secondary"
                  onClick={toggleFeeUpdate}
                >
                  {t(
                    `distribution:feeUpdates.${ManagementFeeDistributionMethodologyEnum.fee_holdback}.add`,
                  )}
                </Button>
              )}
            {!hasFeeUpdate &&
              fundFamilyConfigData.config
                .management_fee_distribution_methodology ==
                ManagementFeeDistributionMethodologyEnum.fee_break_distribution && (
                <Button
                  size="xs"
                  colorScheme="secondary"
                  onClick={toggleFeeUpdate}
                >
                  {t(
                    `distribution:feeUpdates.${ManagementFeeDistributionMethodologyEnum.fee_break_distribution}.add`,
                  )}
                </Button>
              )}
          </TableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          <FundFamilyUseAllocationTable
            tableDef={distributionDef}
            context={{
              getAllocationsByLookupKey,
              getFeeUpdateAllocationsByLookupKey,
              roundingMethodology:
                fundFamilyConfigData.config.rounding_methodology,
            }}
            getAllocation={getFundAllocationForFundFamilyDistributions}
            onRowAdd={onRowAdd}
            onRowRemove={onRowRemove}
            onRowChange={onRowChange}
            onBeforeRowChange={onBeforeRowChange}
            tableData={tableData}
            getErrorForCell={getErrorForCell}
            autoAllocateTooltip={
              hasFeeUpdate
                ? t(
                    `distribution:feeUpdates.${
                      fundFamilyConfigData.config
                        .management_fee_distribution_methodology as "fee_break_distribution"
                    }.autoAllocateTooltip`,
                  )
                : undefined
            }
            amountCellRendererSelector={amountCellRendererSelector}
            columnCellRendererSelector={columnCellRendererSelector}
          />
        </TableBoxBody>
      </TableBox>
      <PageWizardFormFooter />
    </Form>
  );
};

export const CustomFeeBreakDistributionAmountCellRenderer = React.forwardRef<
  HTMLInputElement,
  FundFamilyUseAllocationTableAmountInfoCellRendererProps
>((params, ref) => (
  <FundFamilyUseAllocationTableAmountCellRenderer
    {...params}
    forceReadOnly={
      params.amountTypeDef.id == "remaining_amount" &&
      isFundFamilyDef(params.fundFamilyOrFundDef)
    }
    showIcon={
      !(
        params.amountTypeDef.id === "distribution_amount" &&
        !isFundFamilyDef(params.fundFamilyOrFundDef)
      )
    }
    ref={ref}
  />
));

CustomFeeBreakDistributionAmountCellRenderer.displayName =
  "CustomFeeBreakDistributionAmountCellRenderer";

const columnCellRendererSelector: FundFamilyUseAllocationTableColumnCellRendererSelector =
  (columnDef) => {
    if (columnDef.id === "notes") {
      return CapitalCallNotesCellRenderer;
    }
    return undefined;
  };

export const FundFamilyDistributionAllocationPage: React.FC = () => {
  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();
  const { t } = useTranslation(["common", "distribution", "capitalActivity"]);

  const { data: eventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  if (eventData.state !== FundFamilyEventStateMachineFieldsEnum.draft) {
    return <PageBody />;
  }

  return (
    <>
      <FundFamilyDistributionPageTitle
        title={t("distribution:allocationOfDistribution.title")}
        eventData={eventData}
      />
      <PageBody>
        <VStack alignItems="stretch" mb="40px" spacing="24px">
          <FundFamilyUseAllocationTableForm
            fundFamilyId={fundFamilyId}
            fundFamilyEventId={fundFamilyEventId}
            fundFamilyEventData={eventData}
          />
        </VStack>
      </PageBody>
    </>
  );
};
